import React, { useState, useEffect, useCallback } from "react";
import { useLoading } from "../../../context/LoadingContext";
import {
  fetchData,
  postQuery,
} from "../../Requirement/services/RequirementServices";
import { Modal, Button } from "react-bootstrap";
import "../../../styles/general.css";
import "../../../styles/amtpl.css";

const GenerateOffer = () => {
  const { setLoading } = useLoading();

  // Data states
  const [products, setProducts] = useState([]);
  const [countries, setCountries] = useState([]);
  const [globalPartners, setGlobalPartners] = useState([]);
  const [buyers, setBuyers] = useState([]);

  // Form states
  const [selectedProductName, setSelectedProductName] = useState("");
  const [selectedGrade, setSelectedGrade] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedGlobalPartner, setSelectedGlobalPartner] = useState("");
  const [selectedBuyer, setSelectedBuyer] = useState("");
  const [minOfferQuantityMT, setMinOfferQuantityMT] = useState(0);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [showModalMsg, setShowModalMsg] = useState("");

  // Constants
  const selectedOriginCountry = "India";

  // Fetch Products
  const fetchProducts = useCallback(async () => {
    try {
      const result = await fetchData({
        target: "listProducts",
        userOrder: "ProductName",
        rowStart: 0,
        pageSize: 500,
        filterTerms: { Status: "ACTIVE" },
      });

      if (result && result.products) {
        setProducts(result.products);
      } else if (result && result.items) {
        setProducts(result.items);
      } else {
        console.error("Error fetching products: Invalid response format", result);
        setProducts([]);
      }
    } catch (error) {
      console.error("Error fetching products: ", error);
      setProducts([]);
    }
  }, []);

  // Fetch Countries
  const fetchCountries = useCallback(async () => {
    try {
      const result = await fetchData({
        target: "listDropDownOptions",
        filterTerms: {
          Type: "Country",
        },
      });

      if (result && result.items) {
        setCountries(result.items);
      } else {
        console.error("Error fetching countries: Invalid response format");
        setCountries([]);
      }
    } catch (error) {
      console.error("Error fetching countries: ", error);
      setCountries([]);
    }
  }, []);

  // Fetch AMTPL Users (Global Partners and Buyers)
  const fetchAMTPLUsersSearch = useCallback(async (option, suboption, searchval) => {
    try {
      const result = await fetchData({
        target: "listDynamicDropDownOptions",
        userTable: "AMTPLUsers",
        option: option,
        suboption: suboption,
        searchval: searchval,
        rowStart: 0,
        pageSize: 100,
      });

      if (result && result.items) {
        if (option === "globalpartner") {
          setGlobalPartners(result.items.map((option) => option.result));
        } else if (option === "buyer") {
          setBuyers(result.items.map((option) => option.result));
        }
      } else {
        console.error(`Error fetching ${option}: Invalid response format`);
        if (option === "globalpartner") {
          setGlobalPartners([]);
        } else if (option === "buyer") {
          setBuyers([]);
        }
      }
    } catch (error) {
      console.error(`Error fetching ${option}: `, error);
      if (option === "globalpartner") {
        setGlobalPartners([]);
      } else if (option === "buyer") {
        setBuyers([]);
      }
    }
  }, []);

  // Load product grades based on selected product
  const loadProductGrades = useCallback(async (productName) => {
    if (!productName) return;

    try {
      const result = await fetchData({
        target: "listProducts",
        userOrder: "ProductName",
        rowStart: 0,
        pageSize: 500,
        filterTerms: { ProductName: productName, Status: "ACTIVE" },
      });

      if (result && result.products) {
        // Update the products state to include only relevant grades for filtering
        setProducts(prevProducts => {
          const otherProducts = prevProducts.filter(p => p.ProductName !== productName);
          const currentProductGrades = result.products.filter(p => p.ProductName === productName);
          return [...otherProducts, ...currentProductGrades];
        });
      }
    } catch (error) {
      console.error("Error loading product grades: ", error);
    }
  }, []);

  // Event handlers
  const handleProductChange = (event) => {
    setSelectedProductName(event.target.value);
    setSelectedGrade("");
    // Load grades for the selected product
    if (event.target.value) {
      loadProductGrades(event.target.value);
    }
  };

  const handleGradeChange = (event) => {
    setSelectedGrade(event.target.value);
  };

  const handleCountryChange = (event) => {
    setSelectedCountry(event.target.value);
  };

  const handleGlobalPartnerChange = (event) => {
    let globalPartner = event.target.value;
    setSelectedGlobalPartner(globalPartner);
    fetchAMTPLUsersSearch("buyer", "gpemailid", event.target.value);
  };

  const handleBuyerChange = (event) => {
    setSelectedBuyer(event.target.value);
  };

  const handleMinOfferQuantityMTChange = (event) => {
    setMinOfferQuantityMT(event.target.value);
  };

  const handleGenerateOffer = async () => {
    setLoading(true);
    try {
      const body = {
        target: "generateOffer",
        product: products.find(
          (product) =>
            product.ProductName === selectedProductName &&
            product.Grade === selectedGrade
        ),
        destinationCountry: selectedCountry,
        originCountry: selectedOriginCountry,
        globalPartner: selectedGlobalPartner,
        buyer: selectedBuyer,
        minOfferQuantityMT: minOfferQuantityMT,
      };

      const result = await postQuery(body);

      if (result) {
        setShowModalMsg("Successfully created the aggregated offer.");
        console.log(`Creating automatic offer successful`);
        setShowModal(true);
      } else {
        console.error(`Error while generating offer: ${JSON.stringify(result)}`);
        setShowModalMsg(`Error while generating offer: ${JSON.stringify(result)}`);
        setShowModal(true);
      }
    } catch (error) {
      console.error(`Error while generating offer: ${JSON.stringify(error)}`);
      setShowModalMsg(`Error while generating offer: ${JSON.stringify(error)}`);
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  // Effect hooks
  useEffect(() => {
    fetchProducts();
    fetchCountries();
    fetchAMTPLUsersSearch("globalpartner");
  }, [fetchProducts, fetchCountries, fetchAMTPLUsersSearch]);

  return (
    <div className="amtpl-dashboard ">
      <div className="content-wrapper">
        <main className="main-content">
          <div className="main-content-inner">
            <section className="product-details">
              <div className="tab-content active">
                <div className='row'>
                  <div>
                    <section className="buyer-details">
                      <div className="tab-content active">
                        <div className='row'>
                          <div className='col-lg-9 col-sm-9 col-6'>
                            <div className='row'>
                              <div className='col-lg-3 col-sm-4 col-6'>
                                <label htmlFor="product">Product</label>
                                <select className="form-control form-select" id="product" value={selectedProductName || ''} onChange={handleProductChange}>
                                  <option value="">Select a product</option>
                                  {[...new Set(products.map(product => product.ProductName))].map((product) => (
                                    <option key={product} value={product}>{product}</option>
                                  ))}
                                </select>
                              </div>
                              <div className='col-lg-3 col-sm-4 col-6'>
                                <label htmlFor="grade">Product Grade</label>
                                <select className="form-control form-select" id="grade" value={selectedGrade || ''} onChange={handleGradeChange}>
                                  <option value="">Select a Grade</option>
                                  {products.filter(product => product.ProductName === selectedProductName).map((product) => (
                                    <option key={product.Grade} value={product.Grade}>{product.Grade}</option>
                                  ))}
                                </select>
                              </div>
                              <div className='col-lg-3 col-sm-4 col-6'>
                                <label htmlFor="grade">Destination Countries</label>
                                <select className="form-control form-select" id="grade" value={selectedCountry || ''} onChange={handleCountryChange}>
                                  <option value="">Select a Country</option>
                                  {countries.map((country) => (
                                    <option key={country.Value} value={country.Value}>{country.Value}</option>
                                  ))}
                                </select>
                              </div>
                              <div className='col-lg-3 col-sm-4 col-6'>
                                <label htmlFor="grade">Origin Country</label>
                                <input type="text" readOnly value={selectedOriginCountry} className='form-control' />
                              </div>
                              <div className='col-lg-3 col-sm-4 col-6'>
                                <label htmlFor="grade">Global Partner</label>
                                <select className="form-control form-select" id="globalPartner" name="globalPartner" value={selectedGlobalPartner || ''} onChange={handleGlobalPartnerChange}>
                                  <option value="">Select Global Partner</option>
                                  {[...new Set(buyers)].map((buyer) => {
                                    const match = buyer.match(/<([^>]+)>/);
                                    const keyAndValue = match ? match[1] : '';
                                    return (
                                      <option key={keyAndValue} value={keyAndValue}>{buyer}</option>
                                    );
                                  })}
                                </select>
                              </div>
                              <div className='col-lg-3 col-sm-4 col-6'>
                                <label htmlFor="grade">Buyer</label>
                                <select className="form-control form-select" id="buyer" name="buyer" value={selectedBuyer || ''} onChange={handleBuyerChange}>
                                  <option value="">Select Buyer</option>
                                  {[...new Set(buyers)].map((buyer) => {
                                    const match = buyer.match(/<([^>]+)>/);
                                    const keyAndValue = match ? match[1] : '';
                                    return (
                                      <option key={keyAndValue} value={keyAndValue}>{buyer}</option>
                                    );
                                  })}
                                </select>
                              </div>

                              <div className='col-lg-3 col-sm-4 col-6'>
                                <label>Min Offer Quantity (MT)</label>
                                <input type="text" value={minOfferQuantityMT} className='form-control' onChange={handleMinOfferQuantityMTChange} />
                              </div>
                            </div>
                          </div>
                          <div className='col-lg-3 col-sm-3 col-6'>
                            <div className='actions'>
                              <div className="row">
                                <div className='col-12 mb-2'>
                                  <button
                                    disabled={!selectedProductName || !selectedCountry || !selectedGrade || !selectedOriginCountry || !minOfferQuantityMT || minOfferQuantityMT <= 0}
                                    onClick={handleGenerateOffer}
                                  >
                                    Generate Offer
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>

      {/* Modal for success/error messages */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Notification</Modal.Title>
        </Modal.Header>
        <Modal.Body>{showModalMsg}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GenerateOffer;